import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { debounce } from 'lodash';
import { Flex } from 'antd';
import { motion } from 'motion/react';
import {
  AreaHighlight,
  Content,
  Highlight,
  IHighlight,
  PdfHighlighter,
  PdfLoader,
  Popup,
  ScaledPosition,
} from 'react-pdf-highlighter';
import 'react-pdf-highlighter/dist/style.css';
import { useMediaQuery } from 'react-responsive';
import { RoughNotation } from 'react-rough-notation';
import styled, { useTheme } from 'styled-components';
import {
  Cuepoint,
  CustomTheme,
  IRoughNotation,
  MediaAnalyticsEvents,
  TextSelectionHandler,
} from '../types';
import HighlightZoom from './HighlightZoom';
import { useAnalytics } from '../hooks/useAnalytics';
import { useDocumentImpression } from '../hooks/useDocumentImpression';
import { areHighlightsOverlapping, opacityToHex } from './helpers';
import { filterSeparatorRects } from '@libertify.frontend/helpers';
import { PDF_HIGHLIGHTS_SMOOTH_SCROLL_MAX_PAGE_DISTANCE } from '../constants';
import { PDF_WORKER_SRC } from '../pdfWorker';
import { toSourceZone } from '../hooks/toSourceZone';
import {
  HighlightPopup,
  CustomSpinner,
  PdfZoomControls,
  RoughNotationHighlight,
  RoughNotationConfig,
  DEFAULT_HIGHLIGHT_ROUGHNESS,
} from '@libertify/ui';

const DEFAULT_SCALE_DELTA = 1.1; // 10%
const MIN_SCALE = 0.25;
const MAX_SCALE = 8;
const noop = () => undefined;
const clearTextSelection = () => window.getSelection()?.removeAllRanges();

// Shared beforeLoad/errorMessage fallback — a failed load should look like
// "still loading", not blank (see the errorMessage usage below, MED-6986).
// Module scope: PdfLoader clones this element with an `error` prop when used
// as errorMessage, which this component simply ignores.
const PdfLoadingFallback = () => (
  <Flex
    style={{ width: '100%', height: '100%' }}
    align="center"
    justify="center"
  >
    <CustomSpinner variant="animated" width={320} showLogo />
  </Flex>
);

export const PdfViewer = forwardRef(function PdfViewer(
  {
    url,
    highlights,
    cuepoints,
    isPrimary,
    documentPagesCount,
    isCreatorMode,
    enableDocumentSwitching = false,
    onLoadError,
    onTextSelection,
    isShown = true,
  }: {
    url: string;
    highlights: IHighlight[] | null;
    cuepoints: Cuepoint[] | null;
    isPrimary: boolean;
    documentPagesCount?: number | null;
    isCreatorMode?: boolean;
    /**
     * v2-only: when true, re-detect readiness on `url` change and retry
     * `scrollViewerTo` until the target page loads (needed for switching the
     * displayed document). Off for the legacy viewer, whose behavior is then
     * unchanged.
     */
    enableDocumentSwitching?: boolean;
    /**
     * Called when `PdfLoader` fails to load `url` (e.g. a 403 while the
     * document is still generating server-side). Lets a caller stabilizing
     * the url (see `useStableDocumentUrl`) know to stop holding it (MED-6986).
     */
    onLoadError?: (error: unknown) => void;
    /**
     * Called with each single-page text selection as a `SourceZone`. Area
     * (Alt-drag), cross-page and whitespace-only selections are ignored.
     * Unset → selecting text does nothing, as before.
     * Fires 500ms after the selection stops changing, not on mouse-up, so a
     * pause mid-drag (or mid Shift+arrow) emits a partial zone first: keep
     * only the latest call.
     */
    onTextSelection?: TextSelectionHandler;
    /** False while the document is mounted but hidden (mobile video screen). */
    isShown?: boolean;
  },
  ref,
) {
  const theme = useTheme() as unknown as CustomTheme;
  const scrolledPage = useRef(0);
  const scrolledPages = useRef<number[]>([]);
  const highlightedIds = useRef<string[]>([]);
  const [pdfScaleValue, setPdfScaleValue] = useState<string>('page-width');
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const isMobileLandscape = useMediaQuery({
    query: '(max-width: 1000px) and (orientation: landscape)',
  });
  const scrollViewerTo = useRef<(highlight: IHighlight) => void>(noop);
  const [documentVisibility, setDocumentVisibility] = useState(true);
  const [viewerReady, setViewerReady] = useState(false);
  const pdfHighlighterRef = useRef<PdfHighlighter<IHighlight> | null>(null);
  const [animate, setAnimate] = useState(false);
  const isInitialAnimation = useRef(true);
  const isProgrammaticScroll = useRef(false);
  const [showZoomButtons, setShowZoomButtons] = useState(false);
  const pinchScale = useRef(1);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const viewer = document.querySelector<HTMLDivElement>('.pdfViewer');
  //Get the container of pdfViewer
  const container = viewer?.parentElement;
  const { trackEvent } = useAnalytics();
  useDocumentImpression(viewerReady && isShown);

  const handleSelectionFinished = (
    position: ScaledPosition,
    content: Content,
  ) => {
    // An area (Alt-drag) selection carries an image instead of text.
    if (!onTextSelection || !content.text?.trim()) return;
    // react-pdf-highlighter files a cross-page selection under its first page.
    if (
      position.rects.some((rect) => rect.pageNumber !== position.pageNumber)
    ) {
      return;
    }
    const viewport = pdfHighlighterRef.current?.viewer.getPageView(
      position.pageNumber - 1,
    )?.viewport;
    if (!viewport) return;
    // `content.text` is `range.toString()`, which drops the text layer's line
    // breaks ("accounts,allowing"); the selection keeps them.
    const quote = window.getSelection()?.toString() || content.text;
    onTextSelection(
      toSourceZone(position, quote, viewport),
      clearTextSelection,
    );
  };

  const isScrollingDocument = useRef(false);
  const debouncedTrackEvent = useRef<ReturnType<typeof debounce> | null>(null);

  useEffect(() => {
    debouncedTrackEvent.current = debounce(
      (...args: Parameters<typeof trackEvent>) => {
        trackEvent(...args);
        isScrollingDocument.current = false;
      },
      1000,
      { trailing: true },
    );

    return () => {
      debouncedTrackEvent.current?.cancel();
    };
  }, [trackEvent]);

  const [currentHighlight] = useState<IHighlight | undefined>(undefined);
  const roughness = useMemo(() => {
    const roughnessValue = getComputedStyle(document.documentElement)
      .getPropertyValue('--theme-highlight-roughness')
      .trim();
    // `theme.css` (which declares the var) only ships with the legacy
    // ResponsiveApp/InDocApp path, so on the v2 experience pages the var is
    // absent — fall back to straight rather than to rough-notation's wobbly
    // built-in defaults.
    const parsed = Number.parseFloat(roughnessValue);
    return Number.isNaN(parsed) ? DEFAULT_HIGHLIGHT_ROUGHNESS : parsed;
  }, []);

  // to avoid initial scroll tracking
  useEffect(() => {
    setTimeout(() => {
      isInitialAnimation.current = false;
    }, 4000);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      // get the value in handler to make sure the viewer has been initialized cf MED-4406
      // use safer pagesCount to get the total pages count instead of _pages.length
      // which is not always up to date when streaming the doc
      // use documentPagesCount as fallback from publication data (empty for legacy publications)
      // pdf.js reports 0 pages until the PDF loads, hence || instead of ??
      const totalPages =
        pdfHighlighterRef?.current?.viewer.pagesCount ||
        documentPagesCount ||
        undefined;
      // Skip scroll tracking during initial animation or programmatic scroll
      if (isInitialAnimation.current) {
        return;
      }

      const currentPageNumber =
        pdfHighlighterRef?.current?.viewer._currentPageNumber;

      const props = {
        scroll_y: container?.scrollTop ?? 0,
        current_page: currentPageNumber,
        total_pages: totalPages,
        scroll_source: isProgrammaticScroll.current
          ? ('highlight' as const)
          : ('user' as const),
      };
      if (isScrollingDocument.current) {
        debouncedTrackEvent.current?.(
          MediaAnalyticsEvents.DocumentScrollStopped,
          props,
        );
      } else {
        trackEvent(MediaAnalyticsEvents.DocumentScrollStarted, props);
        isScrollingDocument.current = true;
      }
    };
    container?.addEventListener?.('scroll', onScroll);
    return () => {
      container?.removeEventListener?.('scroll', onScroll);
    };
  }, [container, viewer, animate, documentVisibility, trackEvent]);

  // Control zoom on mobile
  useEffect(() => {
    let initialPinchDistance = 0;
    const zoomFactor = 0.05;
    const minScale = 1;
    const maxScale = 10;

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        const t0 = e.touches[0];
        const t1 = e.touches[1];
        if (!t0 || !t1) return;
        initialPinchDistance = Math.hypot(
          t1.pageX - t0.pageX,
          t1.pageY - t0.pageY,
        );
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (initialPinchDistance <= 0 || e.touches.length < 2) {
        return;
      }
      e.preventDefault(); // Prevent default pinch zoom

      const t0 = e.touches[0];
      const t1 = e.touches[1];
      if (!t0 || !t1) return;
      const pinchDistance = Math.hypot(
        t1.pageX - t0.pageX,
        t1.pageY - t0.pageY,
      );

      if (viewer && container) {
        let newPinchScale = pinchScale.current;

        if (pinchDistance > initialPinchDistance) {
          // Zooming In
          newPinchScale += zoomFactor;
        } else if (pinchDistance < initialPinchDistance) {
          // Zooming Out
          newPinchScale -= zoomFactor;
        }
        initialPinchDistance = pinchDistance;

        newPinchScale = Math.max(minScale, Math.min(newPinchScale, maxScale));

        pinchScale.current = newPinchScale;
        container.style.transform = `scale(${pinchScale.current})`;
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchmove', handleTouchMove, {
      passive: false,
    });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [documentVisibility, container, viewer]);

  // Scroll function based on height
  const scrollToPage = (pageNumber: number) => {
    const viewer = document.querySelector<HTMLDivElement>('.pdfViewer');
    const container = viewer?.parentElement;
    const pageDiv = document.querySelector(
      `[data-page-number="${pageNumber}"]`,
    ) as HTMLElement;

    if (container && pageDiv) {
      const { top: targetTop } = pageDiv.getBoundingClientRect();
      const { top: containerTop } = container.getBoundingClientRect();
      const scrollPosition = targetTop - containerTop + container.scrollTop;
      container.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }
  };

  const hasLandscapePages = () => {
    try {
      const viewer = pdfHighlighterRef.current?.viewer;
      if (!viewer) return;

      // Ensure pages are loaded before calling getPagesOverview
      // getPagesOverview internally calls getViewport on each page which requires pdfPage to be set
      const viewerPages = viewer._pages;
      if (!viewerPages || viewerPages.length === 0) return;

      // Check if viewer is ready - must have getPageView method available
      if (typeof viewer.getPageView !== 'function') return;

      // Only check up to 3 pages since we only need the first 3 for landscape detection
      const pagesToCheck = Math.min(3, viewerPages.length);

      // Check if the first few pages we need are actually loaded
      const hasUnloadedPages = viewerPages
        .slice(0, pagesToCheck)
        .some((page) => !page?.pdfPage);
      if (hasUnloadedPages) return;

      // Additional safety check - verify first page view exists
      const firstPageView = viewer.getPageView(0);
      if (!firstPageView) return;

      const pages = viewer.getPagesOverview();

      // Check if we have landscape within the first three pages
      const hasLandscapePage = pages
        ?.slice(0, 3)
        .some((page) => page.width > page.height);

      if (hasLandscapePage && !isCreatorMode) {
        setPdfScaleValue('page-fit');
      }
    } catch (error) {
      // Viewer not ready yet, ignore the error
      console.debug(
        '[PdfViewer] hasLandscapePages: viewer not ready yet',
        error,
      );
    }
  };

  useEffect(() => {
    // v2 only: when `url` changes (a document swap), the loader re-fetches the
    // PDF, so re-run page detection for the newly-loaded document. The legacy
    // viewer leaves `enableDocumentSwitching` off, so the dep is a constant ''
    // and this runs once on mount, exactly as before. (No synchronous
    // `setViewerReady` reset here — the React-compiler lint rule forbids sync
    // setState in an effect, and it isn't needed: readiness is re-confirmed
    // asynchronously below and the scroll is retry-aware.)
    const checkForPages = () => {
      // Check if pdfPage loaded, then scroll and add animation
      if (document.querySelector('[data-page-number]')) {
        clearInterval(intervalId); // execute only once

        // Wait for the viewer to be ready so highlights render correctly.
        const waitForViewer = () => {
          const viewer = pdfHighlighterRef.current?.viewer;
          if (
            viewer &&
            typeof viewer.getPageView === 'function' &&
            (viewer._pages?.length ?? 0) > 0 &&
            viewer._pages?.[0]?.pdfPage
          ) {
            setViewerReady(true);
            hasLandscapePages();
          } else {
            setTimeout(waitForViewer, 200);
          }
        };
        setTimeout(waitForViewer, 1000);
      }
    };
    // checking every 100ms if pdf loaded
    const intervalId = setInterval(checkForPages, 100);
    return () => clearInterval(intervalId);
    // Legacy (switching off): constant dep → runs once on mount, as before.
    // v2 (switching on): re-runs when the displayed document changes.
  }, [enableDocumentSwitching ? url : '']);

  const scrollToHighlightCentered = useCallback((highlight: IHighlight) => {
    try {
      const viewer = pdfHighlighterRef?.current?.viewer;
      const container = viewer?.container;
      if (!viewer || !highlight?.position?.boundingRect) return;

      // Safety check - ensure viewer is ready
      if (!viewer._pages || viewer._pages.length === 0) return;
      if (typeof viewer.getPageView !== 'function') return;

      const { pageNumber, boundingRect, usePdfCoordinates } =
        highlight.position;

      // Verify the page exists before trying to scroll
      const pageView = viewer.getPageView(pageNumber - 1);
      if (!pageView) return;

      const pageDistance = Math.abs(viewer._currentPageNumber - pageNumber);

      // When long distance, use instant scroll
      if (
        container &&
        pageDistance > PDF_HIGHLIGHTS_SMOOTH_SCROLL_MAX_PAGE_DISTANCE
      ) {
        container.classList.add('scroll-behavior-auto');
        container.addEventListener(
          'scrollend',
          () => {
            container.classList.remove('scroll-behavior-auto');
          },
          { once: true },
        );
      }

      const topMargin = 200; // Margin so the highlight doesn't stick to the viewport top
      let pdfX: number;
      let pdfY: number;
      const pdfPageHeight = pageView.viewport.viewBox[3];

      if (usePdfCoordinates) {
        // Already in PDF space (origin bottom-left), y2 = top edge of highlight
        pdfX = boundingRect.x1;
        pdfY = boundingRect.y2 + topMargin;
      } else {
        // Screen coords (origin top-left) — need to scale AND flip Y axis
        const scale = pdfPageHeight / boundingRect.height;
        pdfX = boundingRect.x1 * scale;
        pdfY = pdfPageHeight - boundingRect.y1 * scale + topMargin;
      }

      viewer.scrollPageIntoView({
        allowNegativeOffset: true,
        pageNumber,
        destArray: [null, { name: 'XYZ' }, pdfX, pdfY, null],
      });
    } catch (error) {
      console.debug(
        '[PdfViewer] scrollToHighlightCentered: error scrolling',
        error,
      );
    }
  }, []);

  useImperativeHandle(ref, () => {
    return {
      scrollViewerTo(highlight: any) {
        // Set flag before programmatic scroll
        isProgrammaticScroll.current = true;

        if (!enableDocumentSwitching) {
          // Legacy: single-shot, unchanged.
          // SCROLL TO PAGE and PAINT HIGHLIGHT ON PAGE
          scrollToHighlightCentered(highlight);
          highlightedIds.current.push(highlight.id);
          // Reset flag after scroll ends
          setTimeout(() => {
            isProgrammaticScroll.current = false;
            // to be sure it is finished
          }, 1500);
          return;
        }

        // v2: retry until the target page view exists. A same-document call
        // lands on the first try; a call issued right after a document swap
        // waits for the newly-loaded PDF's page to become available.
        highlightedIds.current.push(highlight.id);
        const targetPage = highlight?.position?.pageNumber ?? 1;
        const intervalMs = 150;
        const deadlineMs = 8000; // cover a cross-document reload
        let waited = 0;

        const tryScroll = () => {
          const viewer = pdfHighlighterRef.current?.viewer;
          const ready =
            viewer &&
            typeof viewer.getPageView === 'function' &&
            Boolean(viewer.getPageView(targetPage - 1));
          if (ready) {
            scrollToHighlightCentered(highlight);
            setTimeout(() => {
              isProgrammaticScroll.current = false;
            }, 1500);
            return;
          }
          waited += intervalMs;
          if (waited <= deadlineMs) {
            setTimeout(tryScroll, intervalMs);
          } else {
            isProgrammaticScroll.current = false;
          }
        };
        tryScroll();
      },
    };
  }, [scrollToHighlightCentered, enableDocumentSwitching]);

  const zoomToFit = () => {
    if (!pdfHighlighterRef.current) return;
    const newScale = pdfHighlighterRef.current.viewer.currentScale;
    trackEvent(MediaAnalyticsEvents.DocumentZoom, {
      zoom_level: newScale,
      zoom_action: 'zoom_reset',
    });
    setPdfScaleValue('auto');
  };
  const zoomIn = () => {
    if (!pdfHighlighterRef.current) return;
    let newScale = pdfHighlighterRef.current.viewer.currentScale;
    newScale = Number.parseFloat((newScale * DEFAULT_SCALE_DELTA).toFixed(2));
    newScale = Math.ceil(newScale * 10) / 10;
    newScale = Math.min(MAX_SCALE, newScale);
    trackEvent(MediaAnalyticsEvents.DocumentZoom, {
      zoom_level: newScale,
      zoom_action: 'zoom_in',
    });
    setPdfScaleValue(String(newScale));
    // TODO improve, not stable for me (DLY but ok for Diana) but cleaner implementation
    // pdfHighlighterRef.current.viewer.updateScale({ scaleFactor: 1.1 });
    // pdfHighlighterRef.current.containerNodeRef.current?.scroll();
  };

  const zoomOut = () => {
    if (!pdfHighlighterRef.current) return;
    let newScale = pdfHighlighterRef.current.viewer.currentScale;
    newScale = Number.parseFloat((newScale / DEFAULT_SCALE_DELTA).toFixed(2));
    newScale = Math.floor(newScale * 10) / 10;
    newScale = Math.max(MIN_SCALE, newScale);
    trackEvent(MediaAnalyticsEvents.DocumentZoom, {
      zoom_level: newScale,
      zoom_action: 'zoom_out',
    });
    setPdfScaleValue(String(newScale));
  };

  const captureScrollContainer = useCallback((node: HTMLDivElement | null) => {
    if (node && !scrollContainerRef.current) {
      // Wait a tick for PdfHighlighter to render
      //  The requestAnimationFrame ensures the child components have rendered before you query for them.
      requestAnimationFrame(() => {
        const viewer = node?.querySelector<HTMLDivElement>('.pdfViewer');
        const container = viewer?.parentElement as HTMLDivElement;
        if (container) {
          scrollContainerRef.current = container;
        }
      });
    }
  }, []);

  if (!globalThis.window) {
    return null;
  }

  return (
    <PdfLoader
      // Remount on document change instead of updating in place: PdfLoader's
      // componentDidUpdate calls load() without waiting for its async
      // import('pdfjs-dist'), so an in-place url change landing before that
      // chunk resolves dereferences undefined module state and crashes the
      // page (MED-7027). With a key, load() only ever runs from the guarded
      // componentDidMount path. Costs nothing extra: a url change already
      // nulls pdfDocument, which unmounts the whole child tree until the
      // new document arrives.
      key={url}
      url={url}
      // Self-hosted worker — the library's fallback is a hardcoded
      // unpkg.com URL, i.e. a third-party CDN in the critical path of
      // every document view.
      workerSrc={PDF_WORKER_SRC}
      beforeLoad={<PdfLoadingFallback />}
      // A failed load (e.g. a 403 while the document is still generating
      // server-side) would otherwise render nothing — react-pdf-highlighter's
      // PdfLoader falls back to `null` with no `errorMessage` set. Show the
      // same loading state instead, and let the caller un-hold the stable
      // url so the next re-signed url gets picked up (MED-6986).
      errorMessage={<PdfLoadingFallback />}
      onError={onLoadError}
    >
      {(pdfDocument) => {
        return (
          <StyledMotionContainer
            initial={{ y: 0 }}
            animate={
              animate && !isMobile && !isMobileLandscape && isPrimary
                ? {
                    y: [80, 0],
                    transition: {
                      type: 'spring',
                      duration: 2,
                      damping: 5,
                      stiffness: 200,
                    },
                  }
                : { y: 0 }
            }
            style={{
              visibility:
                documentVisibility || isMobile || isMobileLandscape
                  ? 'visible'
                  : 'hidden',
              width: '100%',
              height: '100%',
              willChange: isMobile || isMobileLandscape ? '' : 'transform',
            }}
            onMouseEnter={() => setShowZoomButtons(true)}
            onMouseLeave={() => setShowZoomButtons(false)}
            ref={captureScrollContainer}
          >
            {/* mobile will use pinch to zoom UX */}
            {!isMobile && (
              <PdfZoomControls
                visible={showZoomButtons}
                onZoomIn={zoomIn}
                onZoomOut={zoomOut}
                onZoomToFit={zoomToFit}
              />
            )}
            {
              <PdfHighlighter
                ref={pdfHighlighterRef}
                pdfDocument={pdfDocument}
                enableAreaSelection={(event) => event.altKey}
                pdfScaleValue={pdfScaleValue}
                onScrollChange={noop}
                scrollRef={(scrollTo) => {
                  scrollViewerTo.current = scrollTo;
                }}
                onSelectionFinished={(position, content) => {
                  handleSelectionFinished(position, content);
                  return null;
                }}
                highlightTransform={(
                  highlight,
                  _index,
                  setTip,
                  hideTip,
                  _viewportToScaled,
                  _screenshot,
                  isScrolledTo,
                  // prettier-ignore
                ) => /* NOSONAR */ {
                  const isTextHighlight = !highlight?.content?.image;
                  // load custom roughNotation props
                  // TODO check page
                  const roughNotation: IRoughNotation | null =
                    cuepoints?.find((x) => x.highlightId === highlight.id)
                      ?.roughNotation ?? null;

                  const cuepoint = cuepoints?.find(
                    (x) => x.highlightId === highlight.id,
                  );
                  if (!cuepoint) {
                    // it shouldn't happen with removeHighligthsWithoutCuepoints
                    return <span key={highlight.id} />;
                  }

                  const highlightCorrectFormat = highlights?.find(
                    (highlightx) => highlightx.id === highlight.id,
                  );

                  const hasOverlapsWithCurrentHighlight =
                    currentHighlight?.id !== highlight.id &&
                    areHighlightsOverlapping(
                      highlightCorrectFormat?.position?.boundingRect,
                      currentHighlight?.position?.boundingRect,
                    );

                  const color = roughNotation?.color ?? theme.colors.primary;
                  const colorWithOpacity = hasOverlapsWithCurrentHighlight
                    ? color + opacityToHex(0.2)
                    : color;

                  const highlightPagePassed =
                    highlight.position.pageNumber <
                    // highest page number already read by user except current page
                    Math.max(
                      ...scrolledPages.current.filter(
                        (x) => x === scrolledPage.current,
                      ),
                    );
                  const showAnnotation =
                    highlightedIds.current.includes(highlight.id) ||
                    highlightPagePassed;
                  const zoomAnimated =
                    highlightedIds.current.includes(highlight.id) &&
                    Boolean(cuepoint.zoom);
                  const hasRects = (highlight.position.rects?.length ?? 0) > 0;
                  const isValidForRects =
                    cuepoint.roughNotation &&
                    ['highlight', 'underline', 'strike-through'].includes(
                      cuepoint.roughNotation.type,
                    );
                  const hasImage =
                    highlight.content.image && highlight.content.image !== '-';
                  const shouldRenderRects =
                    hasRects && isValidForRects && !hasImage;
                  const rects = shouldRenderRects
                    ? filterSeparatorRects(highlight.position.rects)
                    : [highlight.position.boundingRect];

                  const component = isTextHighlight ? (
                    cuepoint.roughNotation ? (
                      <RoughNotationHighlight
                        rects={rects}
                        config={
                          {
                            type: 'highlight',
                            animationDelay: 50,
                            padding: 10,
                            ...roughNotation,
                            color: colorWithOpacity,
                          } as RoughNotationConfig
                        }
                        show={showAnnotation}
                        roughness={roughness}
                        opacity={
                          cuepoint.roughNotation?.type === 'highlight'
                            ? 0.35
                            : 1
                        }
                        keyPrefix={`highlight-${highlight.id}`}
                      />
                    ) : (
                      <Highlight
                        isScrolledTo={isScrolledTo}
                        position={highlight.position}
                        comment={highlight.comment}
                      />
                    )
                  ) : cuepoint.roughNotation || cuepoint.zoom ? (
                    <div>
                      {rects.map((rect, highlightIndex) => {
                        // Support both LTWH (left, top) and Scaled (x1, y1) formats
                        const rectLeft =
                          'left' in rect
                            ? (rect.left as number)
                            : (rect as { x1: number }).x1;
                        const rectTop =
                          'top' in rect
                            ? (rect.top as number)
                            : (rect as { y1: number }).y1;

                        return (
                          <div
                            key={'highlight-' + highlightIndex}
                            style={{
                              position: 'absolute',
                              zIndex: 1000,
                              left: rectLeft,
                              top: rectTop,
                              height: rect.height,
                              width: rect.width,
                              fontSize: 10,
                              padding: 2,
                              borderRadius: 3,
                              opacity:
                                cuepoint.roughNotation?.type === 'highlight'
                                  ? 0.35
                                  : 0.5,
                            }}
                          >
                            {!cuepoint?.zoom && (
                              <RoughNotation
                                type="box"
                                animationDelay={200}
                                strokeWidth={3}
                                padding={7}
                                {...roughNotation}
                                color={colorWithOpacity}
                                show={showAnnotation}
                                roughness={roughness}
                              >
                                <div
                                  style={{
                                    height: rect.height,
                                    width: rect.width,
                                  }}
                                ></div>
                              </RoughNotation>
                            )}
                            {cuepoint?.zoom && highlight.content.image && (
                              <HighlightZoom
                                animated={zoomAnimated}
                                src={highlight.content.image}
                                position={highlight.position}
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <AreaHighlight
                      isScrolledTo={isScrolledTo}
                      highlight={highlight}
                      onChange={(boundingRect) => {
                        console.debug('AreaHighlight onChange', boundingRect);
                      }}
                    />
                  );
                  const children = component;
                  const popupWithChildren = ({
                    children,
                    key,
                  }: {
                    children: JSX.Element;
                    key?: string | number;
                  }) => (
                    <Popup
                      key={key}
                      popupContent={<HighlightPopup {...highlight} />}
                      onMouseOver={(popupContent) =>
                        setTip(highlight, (highlight) => popupContent)
                      }
                      onMouseOut={hideTip}
                    >
                      {children}
                    </Popup>
                  );

                  return Array.isArray(children) ? (
                    <>
                      {children.map((child, childIndex) =>
                        popupWithChildren({
                          children: child,
                          key: `${highlight.id}-${childIndex}`,
                        }),
                      )}
                    </>
                  ) : (
                    popupWithChildren({ children, key: highlight.id })
                  );
                }}
                highlights={
                  viewerReady
                    ? removeHighligthsWithoutCuepoints(highlights, cuepoints)
                    : []
                }
              />
            }
          </StyledMotionContainer>
        );
      }}
    </PdfLoader>
  );
});
// some highlights can be configured manually in the editor UI without
// being linked to cuepoint
const removeHighligthsWithoutCuepoints = (
  highlights: IHighlight[] | null,
  cuepoints: Cuepoint[] | null,
) => {
  const cuepointIds = new Set(cuepoints?.map((x) => x.highlightId));
  return highlights?.filter((x) => cuepointIds.has(x.id)) ?? [];
};
const StyledMotionContainer = styled(motion.div)`
  /* pdfViewer parent container (has no specific className) */
  & > div > div {
    scroll-behavior: smooth;
  }

  /* instant jump to far-away highlights, see scrollToHighlightCentered */
  & .scroll-behavior-auto {
    scroll-behavior: auto;
  }
`;
